import ReCAPTCHA from 'react-google-recaptcha';
import { CandidatoService } from './candidato.service';
import { CreateCandidatoDto } from '@/api.types';
import { CreateCandidatoUiDto, getFieldName, normalizeNomeCompleto, validateRequiredFields, } from './inscricao.utils';
import { MutableRefObject, useCallback, useRef } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { ReadProcessoSeletivoDto } from '../processo-seletivo/processo-seletivo.service';
import { SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';
import { clearCandidatoAccessToken, inscricaoRascunhoStorageKey } from './candidato.constants';

function toastErrosCamposBackend(erro: unknown) {
  const anyErr = erro as {
    response?: { data?: { errors?: { field: string }[]; message?: string } };
    message?: string;
  };
  const errors = anyErr.response?.data?.errors;
  if (errors && Array.isArray(errors) && errors.length > 0) {
    let errorMessage = 'Por favor, preencha os seguintes campos obrigatórios:\n\n';
    errorMessage += errors.map((err) => `• ${getFieldName(err.field)}`).join('\n');
    toast.error(errorMessage, {
      autoClose: 10000,
      style: { whiteSpace: 'pre-line' },
    });
    return;
  }
  if (anyErr.message) {
    toast.error(anyErr.message);
    return;
  }
  toast.error(anyErr.response?.data?.message || String(anyErr));
}

export type UseInscricaoSubmitParams = {
  candidatoService: CandidatoService;
  candidatoId: number | null;
  processoSeletivoId: number;
  processoSeletivo: ReadProcessoSeletivoDto | null;
  captchaToken: string | null;
  setCaptchaToken: (v: string | null) => void;
  recaptchaRef: MutableRefObject<ReCAPTCHA | null>;
  setIsLoading: (v: boolean) => void;
  navigate: NavigateFunction;
  /** Ex.: `() => processAndShowErrors({}, data)` */
  onFalhaValidacaoManual: (data: CreateCandidatoUiDto) => void;
};

export function useInscricaoSubmit({
  candidatoService,
  candidatoId,
  processoSeletivoId,
  processoSeletivo,
  captchaToken,
  setCaptchaToken,
  recaptchaRef,
  setIsLoading,
  navigate,
  onFalhaValidacaoManual,
}: UseInscricaoSubmitParams): SubmitHandler<CreateCandidatoUiDto> {
  const onFalhaRef = useRef(onFalhaValidacaoManual);
  onFalhaRef.current = onFalhaValidacaoManual;

  return useCallback(
    async (data) => {
      const validationErrors = validateRequiredFields(data);
      if (validationErrors.length > 0) {
        onFalhaRef.current(data);
        return;
      }

      if (!captchaToken) {
        toast.error(
          'Por favor, complete a verificação do captcha antes de enviar o formulário.',
        );
        return;
      }

      setIsLoading(true);

      try {
        if (candidatoId == null) {
          toast.error('Aguarde a preparação da inscrição ou recarregue a página.');
          setIsLoading(false);
          return;
        }

        const cpfLimpo = data.cpf ? data.cpf.replace(/\D/g, '') : '';
        const cepLimpo = data.endereco?.cep ? data.endereco.cep.replace(/\D/g, '') : undefined;

        const optaVagasPcdValue =
          data.pessoaComDeficiencia === false
            ? undefined
            : typeof data.optaVagasPcd === 'string'
              ? data.optaVagasPcd === 'true'
              : data.optaVagasPcd;

        let documentoIdentificacaoFileId!: string;
        let comprovanteResidenciaFileId!: string;
        let documentoEstudoFileId!: string;
        let provaFileId: string | undefined;
        let documentosComplementaresFileId: string | undefined;
        let documentacaoIndigenaFileId: string | undefined;
        let laudoMedicoFileId: string | undefined;

        const docIdentificacaoFile =
          data.documentoIdentificacaoUrl && typeof data.documentoIdentificacaoUrl !== 'string'
            ? (data.documentoIdentificacaoUrl as File)
            : null;

        if (docIdentificacaoFile) {
          try {
            documentoIdentificacaoFileId = await candidatoService.uploadFile(
              docIdentificacaoFile,
              'documento_identificacao',
              candidatoId,
            );
          } catch (error) {
            console.error('Erro ao fazer upload do documento de identificação:', error);
            toast.error('Erro ao fazer upload do documento de identificação. Tente novamente.');
            setIsLoading(false);
            return;
          }
        } else if (
          typeof data.documentoIdentificacaoUrl === 'string' &&
          data.documentoIdentificacaoUrl.trim() !== ''
        ) {
          documentoIdentificacaoFileId = data.documentoIdentificacaoUrl as any;
        } else {
          toast.error('Por favor, anexe o documento de identificação (RG).');
          setIsLoading(false);
          return;
        }

        const comprovanteFile =
          data.comprovanteResidenciaUrl && typeof data.comprovanteResidenciaUrl !== 'string'
            ? (data.comprovanteResidenciaUrl as File)
            : null;

        if (comprovanteFile) {
          try {
            comprovanteResidenciaFileId = await candidatoService.uploadFile(
              comprovanteFile,
              'comprovante_residencia',
              candidatoId,
            );
          } catch (error) {
            console.error('Erro ao fazer upload do comprovante de residência:', error);
            toast.error('Erro ao fazer upload do comprovante de residência. Tente novamente.');
            setIsLoading(false);
            return;
          }
        } else if (
          typeof data.comprovanteResidenciaUrl === 'string' &&
          data.comprovanteResidenciaUrl.trim() !== ''
        ) {
          comprovanteResidenciaFileId = data.comprovanteResidenciaUrl as any;
        } else {
          toast.error('Por favor, anexe o comprovante de residência.');
          setIsLoading(false);
          return;
        }

        const docEstudoFile =
          data.documentoEstudoUrl && typeof data.documentoEstudoUrl !== 'string'
            ? (data.documentoEstudoUrl as File)
            : null;

        if (docEstudoFile) {
          try {
            documentoEstudoFileId = await candidatoService.uploadFile(
              docEstudoFile,
              'documento_estudo',
              candidatoId,
            );
          } catch (error) {
            console.error('Erro ao fazer upload do documento de estudo:', error);
            toast.error('Erro ao fazer upload do documento de estudo. Tente novamente.');
            setIsLoading(false);
            return;
          }
        } else if (
          typeof data.documentoEstudoUrl === 'string' &&
          data.documentoEstudoUrl.trim() !== ''
        ) {
          documentoEstudoFileId = data.documentoEstudoUrl as any;
        } else {
          toast.error('Por favor, anexe o documento de estudo da faculdade.');
          setIsLoading(false);
          return;
        }

        if (processoSeletivo?.comProva === true) {
          const provaFile =
            data.provaUrl && typeof data.provaUrl !== 'string'
              ? (data.provaUrl as File)
              : null;

          if (provaFile) {
            try {
              provaFileId = await candidatoService.uploadFile(provaFile, 'prova', candidatoId);
            } catch (error) {
              console.error('Erro ao fazer upload da prova:', error);
              toast.error('Erro ao fazer upload da prova. Tente novamente.');
              setIsLoading(false);
              return;
            }
          } else if (typeof data.provaUrl === 'string' && data.provaUrl.trim() !== '') {
            provaFileId = data.provaUrl as any;
          }
        }

        const docComplementaresFile =
          data.documentosComplementaresUrl &&
            typeof data.documentosComplementaresUrl !== 'string'
            ? (data.documentosComplementaresUrl as File)
            : null;

        if (docComplementaresFile) {
          try {
            documentosComplementaresFileId = await candidatoService.uploadFile(
              docComplementaresFile,
              'documentos_complementares',
              candidatoId,
            );
          } catch (error) {
            console.error('Erro ao fazer upload dos documentos complementares:', error);
            toast.error('Erro ao fazer upload dos documentos complementares. Tente novamente.');
            setIsLoading(false);
            return;
          }
        } else if (
          typeof data.documentosComplementaresUrl === 'string' &&
          data.documentosComplementaresUrl.trim() !== ''
        ) {
          documentosComplementaresFileId = data.documentosComplementaresUrl as any;
        }

        const docIndigenaFile =
          data.documentacaoIndigenaUrl && typeof data.documentacaoIndigenaUrl !== 'string'
            ? (data.documentacaoIndigenaUrl as File)
            : null;

        const optaVagasIndigenas = data.raca === 'indigena' && data.optaVagasNegros === true;

        if (optaVagasIndigenas && docIndigenaFile) {
          try {
            documentacaoIndigenaFileId = await candidatoService.uploadFile(
              docIndigenaFile,
              'documentacao_indigena',
              candidatoId,
            );
          } catch (error) {
            console.error('Erro ao fazer upload da documentação indígena:', error);
            toast.error('Erro ao fazer upload da documentação indígena. Tente novamente.');
            setIsLoading(false);
            return;
          }
        }

        const laudoFile =
          data.laudoMedicoUrl && typeof data.laudoMedicoUrl !== 'string'
            ? (data.laudoMedicoUrl as File)
            : null;

        if (optaVagasPcdValue && laudoFile) {
          try {
            laudoMedicoFileId = await candidatoService.uploadFile(
              laudoFile,
              'laudo_medico',
              candidatoId,
            );
          } catch (error) {
            console.error('Erro ao fazer upload do laudo médico:', error);
            toast.error('Erro ao fazer upload do laudo médico. Tente novamente.');
            setIsLoading(false);
            return;
          }
        }

        const telefoneLimpo = data.telefone
          ? {
            ddd: data.telefone.ddd
              ? Number(data.telefone.ddd.replace(/\D/g, ''))
              : undefined,
            numero: data.telefone.numero
              ? data.telefone.numero.replace(/\D/g, '')
              : undefined,
          }
          : undefined;

        const celularLimpo = data.celular
          ? {
            ddd: data.celular.ddd ? Number(data.celular.ddd.replace(/\D/g, '')) : undefined,
            numero: data.celular.numero ? data.celular.numero.replace(/\D/g, '') : undefined,
          }
          : undefined;

        const instituicaoEnsinoIdStr = String(data.instituicaoEnsinoId ?? '');
        const temOutrosComNome =
          data.instituicaoEnsinoNome != null &&
          String(data.instituicaoEnsinoNome).trim() !== '';

        let instituicaoEnsinoIdFinal: number | undefined;
        let instituicaoEnsinoNomeFinal: string | undefined;

        if (
          instituicaoEnsinoIdStr === 'outros' ||
          (instituicaoEnsinoIdStr === '' && temOutrosComNome)
        ) {
          if (!data.instituicaoEnsinoNome || data.instituicaoEnsinoNome.trim() === '') {
            toast.error(
              'Por favor, informe o nome da instituição de ensino quando escolher "Outros".',
            );
            setIsLoading(false);
            return;
          }
          instituicaoEnsinoIdFinal = undefined;
          instituicaoEnsinoNomeFinal = data.instituicaoEnsinoNome.trim();
        } else if (instituicaoEnsinoIdStr !== '' && instituicaoEnsinoIdStr !== 'undefined') {
          const idNumero =
            typeof data.instituicaoEnsinoId === 'number'
              ? data.instituicaoEnsinoId
              : typeof data.instituicaoEnsinoId === 'string' &&
                data.instituicaoEnsinoId !== '' &&
                data.instituicaoEnsinoId !== 'outros'
                ? Number(data.instituicaoEnsinoId)
                : undefined;
          instituicaoEnsinoIdFinal =
            idNumero != null && !isNaN(idNumero) ? idNumero : undefined;
          instituicaoEnsinoNomeFinal = undefined;
          if (instituicaoEnsinoIdFinal == null) {
            toast.error('Por favor, selecione uma instituição de ensino ou escolha "Outros".');
            setIsLoading(false);
            return;
          }
        } else {
          toast.error('Por favor, selecione uma instituição de ensino ou escolha "Outros".');
          setIsLoading(false);
          return;
        }

        const { emailConfirmacao, ...dataSemConfirmacao } = data;

        const nomePessoa = normalizeNomeCompleto(data.nome || '');

        if (!celularLimpo) {
          toast.error('Por favor, informe o celular.');
          setIsLoading(false);
          return;
        }

        if (!data.endereco) {
          toast.error('Por favor, informe o endereço.');
          setIsLoading(false);
          return;
        }

        const municipioFinal = (data.municipio ?? '').trim();
        if (!municipioFinal) {
          toast.error('Por favor, informe o município.');
          setIsLoading(false);
          return;
        }

        const dadosEnvio: CreateCandidatoDto = {
          ...(dataSemConfirmacao as any),
          nome: nomePessoa,
          captchaToken: captchaToken || undefined,
          cpf: cpfLimpo,
          telefone: telefoneLimpo,
          celular: celularLimpo,
          instituicaoEnsinoId: instituicaoEnsinoIdFinal,
          instituicaoEnsinoNome: instituicaoEnsinoNomeFinal,
          endereco: {
            ...data.endereco,
            cep: cepLimpo,
          },
          municipio: municipioFinal,
          optaVagasNegros:
            typeof data.optaVagasNegros === 'string'
              ? data.optaVagasNegros === 'true'
              : data.optaVagasNegros || false,
          declaraIndigena: data.raca === 'indigena' && data.optaVagasNegros === true,
          pessoaComDeficiencia: data.pessoaComDeficiencia,
          optaVagasPcd: optaVagasPcdValue,
          descricaoDeficiencia:
            data.pessoaComDeficiencia === false ? undefined : data.descricaoDeficiencia,
          necessitaCuidadosEspeciais:
            data.pessoaComDeficiencia === false
              ? undefined
              : typeof data.necessitaCuidadosEspeciais === 'string'
                ? data.necessitaCuidadosEspeciais === 'true'
                : data.necessitaCuidadosEspeciais,
          declaracaoVeracidade: data.declaracaoVeracidade || false,
          confirmacaoEdital: data.confirmacaoEdital || false,
          consentimentoLgpd: data.consentimentoLgpd || false,
          documentoIdentificacaoUrl: documentoIdentificacaoFileId,
          comprovanteResidenciaUrl: comprovanteResidenciaFileId,
          documentoEstudoUrl: documentoEstudoFileId,
          provaUrl: provaFileId,
          documentosComplementaresUrl: documentosComplementaresFileId,
          documentacaoIndigenaUrl: documentacaoIndigenaFileId,
          laudoMedicoUrl: data.pessoaComDeficiencia === false ? undefined : laudoMedicoFileId,
          comarcaPretendidaId:
            data.comarcaPretendidaId && data.comarcaPretendidaId > 0
              ? data.comarcaPretendidaId
              : undefined,
        };

        const optaVagasIndigenasFinal =
          dadosEnvio.raca === 'indigena' && dadosEnvio.optaVagasNegros === true;
        if (!optaVagasIndigenasFinal || !documentacaoIndigenaFileId) {
          delete dadosEnvio.documentacaoIndigenaUrl;
        }

        if (!optaVagasPcdValue || !laudoMedicoFileId) {
          delete dadosEnvio.laudoMedicoUrl;
        }

        if (processoSeletivo?.comProva !== true || !provaFileId) {
          delete dadosEnvio.provaUrl;
        }

        if (!dadosEnvio.comarcaPretendidaId || dadosEnvio.comarcaPretendidaId <= 0) {
          delete dadosEnvio.comarcaPretendidaId;
        }

        try {
          await candidatoService.updatePublic(candidatoId, dadosEnvio);
          toast.success('Inscrição enviada com sucesso!');
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          setCaptchaToken(null);

          const rascunhoKey = inscricaoRascunhoStorageKey(processoSeletivoId);
          const concluiuRascunhoNestaSessao =
            sessionStorage.getItem(rascunhoKey) === String(candidatoId);
          sessionStorage.removeItem(rascunhoKey);
          clearCandidatoAccessToken(candidatoId);

          if (concluiuRascunhoNestaSessao) {
            const emailCandidato = data.email || '';
            const cpfCandidato = data.cpf ? data.cpf.replace(/\D/g, '') : '';
            const nomeCandidato = nomePessoa;
            const params = new URLSearchParams();
            if (emailCandidato) params.append('email', emailCandidato);
            if (cpfCandidato) params.append('cpf', cpfCandidato);
            if (nomeCandidato) params.append('nome', nomeCandidato);
            navigate(
              `/candidato/inscricao-confirmacao${params.toString() ? `?${params.toString()}` : ''}`,
            );
          } else {
            navigate('/candidato/meus-processos');
          }
        } catch (updateError: unknown) {
          const err = updateError as {
            response?: { data?: { errors?: { field: string }[]; message?: string } };
          };
          if (
            err.response?.data?.errors &&
            Array.isArray(err.response.data.errors) &&
            err.response.data.errors.length > 0
          ) {
            let errorMessage = 'Por favor, preencha os seguintes campos obrigatórios:\n\n';
            errorMessage += err.response.data.errors
              .map((e: { field: string }) => `• ${getFieldName(e.field)}`)
              .join('\n');
            toast.error(errorMessage, {
              autoClose: 10000,
              style: { whiteSpace: 'pre-line' },
            });
          } else {
            toast.error(
              err.response?.data?.message != null
                ? String(err.response.data.message)
                : String(updateError),
            );
          }
          setIsLoading(false);
          return;
        }
      } catch (error: unknown) {
        toastErrosCamposBackend(error);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
      } finally {
        setIsLoading(false);
      }
    },
    [
      candidatoService,
      candidatoId,
      captchaToken,
      navigate,
      processoSeletivo,
      processoSeletivoId,
      recaptchaRef,
      setCaptchaToken,
      setIsLoading,
    ],
  );
}
